import React, { useEffect, useRef, useState } from 'react';
import { formatDate } from '../utils/format';
import { downloadFileAttachment } from '../utils/util';
import CustomIconButton from './CustomIconButton';

interface FileItemProps {
  file: any;
  handleClearFile?: () => void;
  onFileClick?: () => void;
  showDeleteButton?: boolean;
  displayBorder?: boolean;
  mobileBorderOnly?: boolean;
}

/**
 * Smart truncate filename based on REAL container width
 * Example:
 * phongcongchungketoan.pdf
 * =>
 * phongcongchung...an.pdf
 */

const SmartFileName: React.FC<{ fileName: string, setIsTruncated:() => {} }> = ({ fileName, setIsTruncated }) => {
  const containerRef = useRef<HTMLParagraphElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  const [displayName, setDisplayName] = useState(fileName);

  useEffect(() => {
    if (!containerRef.current || !measureRef.current) return;

    const container = containerRef.current;
    const measure = measureRef.current;

    const truncate = () => {
      const containerWidth = container.offsetWidth;

      // Reset to original
      measure.textContent = fileName;

      // Already fits
      if (measure.offsetWidth <= containerWidth) {
        setDisplayName(fileName);
        setIsTruncated(false);
        return;
      }
      setIsTruncated(true);

      // Split extension
      const lastDotIndex = fileName.lastIndexOf('.');

      let name = fileName;
      let extension = '';

      if (lastDotIndex !== -1) {
        name = fileName.slice(0, lastDotIndex);
        extension = fileName.slice(lastDotIndex);
      }

      // Keep last 2 chars before extension
      const endChars = name.slice(-2);

      // Start shrinking beginning
      let startLength = name.length;

      while (startLength > 1) {
        const start = name.slice(0, startLength);

        const candidate = `${start}...${endChars}${extension}`;

        measure.textContent = candidate;

        if (measure.offsetWidth <= containerWidth) {
          setDisplayName(candidate);
          return;
        }

        startLength--;
      }

      // Fallback
      setDisplayName(`...${endChars}${extension}`);
    };

    truncate();

    const resizeObserver = new ResizeObserver(() => {
      truncate();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [fileName]);

  return (
    <>
      {/* visible text */}
      <p
        ref={containerRef}
        className="underline underline-offset-2 alu-text-base-2 group-hover:text-[#FF861A] transition-colors overflow-hidden whitespace-nowrap"
      >
        {displayName}
      </p>

      {/* hidden measuring element */}
      <span
        ref={measureRef}
        className="absolute invisible whitespace-nowrap alu-text-base-2 font-inherit"
      />
    </>
  );
};

const FileItem: React.FC<FileItemProps> = ({
  file,
  handleClearFile,
  onFileClick,
  showDeleteButton = true,
  displayBorder = true,
  mobileBorderOnly = false,
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileName = file?.file_name || '';
  const uploadedAt = file?.created_at || '';

  const filePath = file?.file_path || '';

  const borderClass = mobileBorderOnly
    ? 'border border-[#E0E0E0] rounded-lg p-4 md:border-0 md:p-0'
    : displayBorder
    ? 'border border-[#E0E0E0] rounded-lg p-4'
    : '';

  const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return;

    setIsLoading(true);
    try {
      if (onFileClick) {
        await onFileClick();
        return;
      }

      const lowerName = fileName.toLowerCase();
      const isPdf = lowerName.endsWith('.pdf') || filePath.toLowerCase().endsWith('.pdf');

      if (isPdf) {
        // Open PDF preview in new tab
        window.open(filePath, '_blank');
        return;
      }

      // Use the original filename for downloads for non-PDF files
      await downloadFileAttachment(filePath, fileName || undefined);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`w-full min-w-0 ${borderClass} flex items-center justify-between gap-3`}
    >
      <div className="w-full min-w-0 overflow-hidden">
        <div
          onClick={handleClick}
          className={`${isTruncated ? "w-full" : "max-w-fit"} min-w-0 overflow-hidden flex items-center gap-[0.375rem] text-sm ${uploadedAt && "mb-1"} group hover:cursor-pointer ${isLoading ? "pointer-events-none opacity-60" : ""}`}
        >
          {/* file icon */}
          <span className="shrink-0 group-hover:text-[#FF861A] transition-colors">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <mask
                id="mask0_6895_15272"
                style={{ maskType: 'alpha' }}
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="20"
                height="20"
              >
                <rect width="20" height="20" fill="#D9D9D9" />
              </mask>

              <g mask="url(#mask0_6895_15272)">
                <path
                  d="M5.00041 18.3332C4.54207 18.3332 4.14971 18.17 3.82332 17.8436C3.49693 17.5173 3.33374 17.1249 3.33374 16.6666V3.33323C3.33374 2.8749 3.49693 2.48254 3.82332 2.15615C4.14971 1.82976 4.54207 1.66656 5.00041 1.66656H11.6671L16.6671 6.66656V16.6666C16.6671 17.1249 16.5039 17.5173 16.1775 17.8436C15.8511 18.17 15.4587 18.3332 15.0004 18.3332H5.00041ZM10.8337 7.4999V3.33323H5.00041V16.6666H15.0004V7.4999H10.8337Z"
                  fill="currentColor"
                />
              </g>
            </svg>
          </span>

          {/* filename */}
          <div className="flex-1 min-w-0">
            <SmartFileName fileName={fileName} setIsTruncated={setIsTruncated} />
          </div>
        </div>

        {/* upload date */}
        {uploadedAt && <p className="text-xs alu-text-base-1">
          アップロード日：{formatDate(uploadedAt)} 更新
        </p>}
      </div>

      {/* delete button */}
      {showDeleteButton && handleClearFile && (
        <CustomIconButton
          type="button"
          customStyle="shrink-0"
          onClick={handleClearFile}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <g clipPath="url(#clip0_8574_265)">
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="currentColor"
              />
            </g>

            <defs>
              <clipPath id="clip0_8574_265">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </CustomIconButton>
      )}
    </div>
  );
};

export default FileItem;